import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AdminClientProvider } from "./context/AdminClientContext";
import { ReportingModeProvider } from "./context/ReportingModeContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Layout } from "./components/Layout";
import MFAEnforcementGate from "./components/MFAEnforcementGate";
import ErrorBoundary from "./components/ErrorBoundary";

// Pages
import Login from "./pages/Login";
// Register page removed - admin-only user creation
import Dashboard from "./pages/Dashboard";
import Populations from "./pages/Populations";
import Stimuli from "./pages/Stimuli";
import Simulations from "./pages/Simulations";
import Analysis from "./pages/Analysis";
import ExecutiveView from "./pages/ExecutiveView";
import Compare from "./pages/Compare";
import Settings from "./pages/Settings";
import Admin from "./pages/Admin";
import Guide from "./pages/Guide";

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground text-sm mt-3">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <MFAEnforcementGate>
      <Layout>{children}</Layout>
    </MFAEnforcementGate>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground text-sm mt-3">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <MFAEnforcementGate>
      <Layout>{children}</Layout>
    </MFAEnforcementGate>
  );
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Chromeless Protected Route — auth-gated but renders without sidebar/Layout.
// Used for boardroom/PDF views like /analysis/executive.
const ChromelessProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <MFAEnforcementGate>{children}</MFAEnforcementGate>;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      {/* Register removed - admin-only user creation */}

      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/guide" element={<ProtectedRoute><Guide /></ProtectedRoute>} />
      <Route path="/populations" element={<ProtectedRoute><Populations /></ProtectedRoute>} />
      <Route path="/stimuli" element={<ProtectedRoute><Stimuli /></ProtectedRoute>} />
      <Route path="/simulations" element={<ProtectedRoute><Simulations /></ProtectedRoute>} />
      <Route path="/analysis" element={<ProtectedRoute><Analysis /></ProtectedRoute>} />
      <Route
        path="/analysis/executive"
        element={
          <ChromelessProtectedRoute>
            <ExecutiveView />
          </ChromelessProtectedRoute>
        }
      />
      <Route path="/compare" element={<ProtectedRoute><Compare /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />

      {/* Redirects */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AdminClientProvider>
              <ReportingModeProvider>
                <AppRoutes />
                <Toaster position="top-right" />
              </ReportingModeProvider>
            </AdminClientProvider>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
